import * as React from "react";
import { Dialog, Title, Size, Text, Paragraph, Button, TextColor } from "@root/shared";
import { Grid, RowCol } from "@root/shared/grid";
import { noop } from "lodash";
import { githubUrl } from "./data/constants";

const githubAppUrl =
  (window as any).initProps.config.env === "staging" // the staging webpack config uses the prod NODE_ENV, so we have to check this at runtime
    ? githubUrl.staging
    : process.env.NODE_ENV === "production" // this gets compiled out at compile-time due to Webpack.DefinePlugin
    ? githubUrl.production
    : githubUrl.development;

type ErrorDialogProps = {
  title: string;
  body: React.ReactNode;
  retryable?: boolean;
};
const ErrorDialog: React.SFC<ErrorDialogProps> = ({ title, body, retryable = true }) => (
  <Dialog
    data-test-id="error-dialog"
    visible={true}
    onRequestClose={noop}
    width={520}
    children={
      <Grid>
        <RowCol center>
          <img role="presentation" aria-hidden tabIndex={-1} src={require("./stargazer.svg")} />
        </RowCol>
        <RowCol center>
          <Title size={Size.Medium}>{title}</Title>
        </RowCol>
        <RowCol center width={10}>
          <Paragraph size={Size.Medium} color={TextColor.Secondary}>
            {body}
          </Paragraph>
        </RowCol>
        {retryable && (
          <RowCol center>
            <Button href={githubAppUrl}>Use other GitHub account</Button>
          </RowCol>
        )}
      </Grid>
    }
  />
);

export const RepositoriesErrorDialog: React.SFC<{ githubUsername: string }> = ({ githubUsername }) => (
  <ErrorDialog
    title="Can’t find any repository."
    body={
      <>
        No repositories were found in your GitHub account
        {githubUsername && <Text size={Size.Medium} bold>{` @${githubUsername}`}</Text>}. Are you sure you chose the correct
        GitHub&nbsp;account?
      </>
    }
  />
);

export const UserMatchErrorDialog: React.SFC<{ githubUsername: string }> = ({ githubUsername }) => (
  <ErrorDialog
    title="Can’t use this GitHub account."
    body={
      <>
        Your App Center account is being used with another GitHub account
        {githubUsername && <Text size={Size.Medium} bold>{`, so it can’t be used with @${githubUsername}`}</Text>}.
      </>
    }
  />
);

export const OrgPermissionErrorDialog: React.SFC<{ orgName: string }> = ({ orgName }) => (
  <ErrorDialog
    title={`${orgName ? orgName : "An organization with this name"} already exists.`}
    body="Your organization already exists in App Center but you are not a member yet. Please ask the administrators to be added so that you can add apps to it."
    retryable={false}
  />
);

export const BillingPlanErrorDialog: React.SFC = () => (
  <ErrorDialog
    title="Can’t connect to GitHub."
    body={<>We’re having trouble loading your information. Are you sure you chose the correct GitHub&nbsp;account?</>}
  />
);
